//#region Imports

import { Component, inject, OnInit } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { AuthService } from '../../shared/services/auth/auth.service';
import { AnimatedBannerComponent } from '../../shared/components/animated-banner/animated-banner.component';
import { PasswordFeedbackComponent } from '../../shared/components/password-feedback/password-feedback.component';
import { ButtonTypeEnum } from '../../shared/components/button/types/button-type.enum';
import { ButtonRadiusEnum } from '../../shared/components/button/types/button-radius.enum';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { SpinnerSizeEnum } from '../../shared/components/spinner/types/spinner-size.enum';
import { SpinnerColorEnum } from '../../shared/components/spinner/types/spinner-color.enum';

//#endregion

export type FirstAccessStep = 'validating' | 'form' | 'success' | 'expired' | 'invalid' | 'unavailable';

interface FirstAccessForm {
  password: FormControl<string>;
  confirmPassword: FormControl<string>;
}

const MIN_PASSWORD_LENGTH = 6;

const DEFAULT_INVALID_DESCRIPTION = 'Este link não é válido ou já foi utilizado. Se você já criou sua senha, acesse sua conta pela tela de login.';

const DEFAULT_SUBMIT_ERROR = 'Verifique sua conexão e tente novamente.';

@Component({
  selector: 'l-first-access',
  templateUrl: './first-access.component.html',
  styleUrl: './first-access.component.scss',
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    IconComponent,
    AnimatedBannerComponent,
    PasswordFeedbackComponent,
    SpinnerComponent,
  ],
})
export class FirstAccessComponent implements OnInit {

  public step: FirstAccessStep = 'validating';

  public isSubmitting = false;

  public submitted = false;

  public submitError: string | null = null;

  public invalidDescription = DEFAULT_INVALID_DESCRIPTION;

  public hidePassword = true;

  public hideConfirmPassword = true;

  public readonly buttonTypes = ButtonTypeEnum;

  public readonly buttonRadius = ButtonRadiusEnum;

  public readonly spinnerSize = SpinnerSizeEnum;

  public readonly spinnerColors = SpinnerColorEnum;

  public readonly form = new FormGroup<FirstAccessForm>({
    password: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)] }),
    confirmPassword: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  private token = '';

  private email = '';

  private readonly authService = inject(AuthService);

  private readonly route = inject(ActivatedRoute);

  private readonly router = inject(Router);

  public get passwordHasError(): boolean {
    return this.submitted && this.form.controls.password.value.length < MIN_PASSWORD_LENGTH;
  }

  public get confirmPasswordHasError(): boolean {
    const { password, confirmPassword } = this.form.getRawValue();

    return (this.submitted || !!confirmPassword) && password !== confirmPassword;
  }

  public async ngOnInit(): Promise<void> {
    const params = this.route.snapshot.queryParamMap;

    this.token = (params.get('token') ?? '').trim();
    this.email = (params.get('email') ?? '').trim();

    await this.validateLink();
  }

  public async validateLink(): Promise<void> {
    if (!this.token || !this.email) {
      this.step = 'invalid';
      return;
    }

    this.step = 'validating';

    try {
      await this.authService.getFirstAccess(this.token, this.email);
      this.step = 'form';
    } catch (error: unknown) {
      this.step = this.resolveLinkErrorStep(error) ?? 'unavailable';
    }
  }

  public async submit(): Promise<void> {
    if (this.isSubmitting) {
      return;
    }

    this.submitted = true;

    const { password, confirmPassword } = this.form.getRawValue();

    if (password.length < MIN_PASSWORD_LENGTH || password !== confirmPassword) {
      return;
    }

    this.submitError = null;
    this.setSubmitting(true);

    try {
      await this.authService.defineFirstAccessPassword({
        token: this.token,
        email: this.email,
        newPassword: password,
        confirmPassword,
      });
      this.step = 'success';
    } catch (error: unknown) {
      const linkErrorStep = this.resolveLinkErrorStep(error, true);

      if (linkErrorStep) {
        this.step = linkErrorStep;
        return;
      }

      this.submitError = this.getBadRequestMessage(error) ?? DEFAULT_SUBMIT_ERROR;
    } finally {
      this.setSubmitting(false);
    }
  }

  public goToLogin(): void {
    void this.router.navigate(['/login'], {
      queryParams: this.email ? { email: this.email } : {},
    });
  }

  public goToForgotPassword(): void {
    void this.router.navigateByUrl('/forgot/password');
  }

  /**
   * Converte o erro da IAM no estado de link correspondente
   *
   * @param error O erro retornado pela API
   * @param onlyKnownMessages Quando verdadeiro, só trata como link inválido os 400 que falam do link,
   * para não confundir com erros de validação da senha
   */
  private resolveLinkErrorStep(error: unknown, onlyKnownMessages = false): FirstAccessStep | null {
    const status = (error as { status?: number })?.status;
    const message = this.getApiMessage(error);

    if (status === HttpStatusCode.BadRequest && /expirou/i.test(message)) {
      return 'expired';
    }

    const isUnusableLink = /utilizado|já foi definida|suspensa/i.test(message);

    if (status === HttpStatusCode.NotFound || (status === HttpStatusCode.BadRequest && (isUnusableLink || !onlyKnownMessages))) {
      this.invalidDescription = /suspensa/i.test(message) ? message : DEFAULT_INVALID_DESCRIPTION;
      return 'invalid';
    }

    return null;
  }

  private getBadRequestMessage(error: unknown): string | null {
    const status = (error as { status?: number })?.status;

    return status === HttpStatusCode.BadRequest ? this.getApiMessage(error) || null : null;
  }

  private getApiMessage(error: unknown): string {
    const message = (error as { error?: { message?: string | string[] } })?.error?.message;

    return Array.isArray(message) ? (message[0] ?? '') : (message ?? '');
  }

  private setSubmitting(isSubmitting: boolean): void {
    this.isSubmitting = isSubmitting;

    if (isSubmitting) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

}
